import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LiveCoursePreview from '../components/LiveCoursePreview';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import { Sparkles, BookOpen, Code, Globe, MessageCircle } from 'lucide-react';
import SessionManager from '../utils/sessionManager';

const CoursePreviewPage = () => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [candidateInfo, setCandidateInfo] = useState(null);

  useEffect(() => {
    // Mark free trial as used in DB
    const session = SessionManager.getSession('candidate');
    const candidateId = session?.user_id;
    const token = session?.access_token;
    if (candidateId && token) {
      fetch(`${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/candidates/${candidateId}/mark-free-trial`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    }

    // Get candidate info from SessionManager
    
    if (session && session.profile) {
      setCandidateInfo({
        id: session.user_id || session.profile.id,
        name: session.profile.full_name || 
              (session.profile.first_name && session.profile.last_name 
                ? `${session.profile.first_name} ${session.profile.last_name}` 
                : session.profile.username || 'Student'),
        email: session.email || session.profile.email
      });
      console.log('✅ User info from session:', session);
    } else {
      console.log('❌ No session found');
    }
  }, []);

  const handleStartPreview = () => {
    console.log('🎯 Starting preview, candidateInfo:', candidateInfo);
    if (!candidateInfo) {
      alert('Please sign in to start the preview');
      navigate('/login');
      return;
    }
    setShowPreview(true);
  };

  const handlePreviewEnd = () => {
    setShowPreview(false);
    // Optionally redirect to courses page
    navigate('/courses');
  };

  if (showPreview && candidateInfo) {
    return (
      <LiveCoursePreview
        candidateName={candidateInfo.name}
        candidateEmail={candidateInfo.email}
        candidateId={candidateInfo.id}
        duration={4}
        onPreviewEnd={handlePreviewEnd}
        onExit={() => setShowPreview(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <LandingHeader />
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">Free 4-Minute Preview</span>
          </div>
          
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Experience Live AI Teaching
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            See how Aptirix's AI teacher makes learning interactive, engaging, and personalized. 
            Try our live teaching platform for free - no credit card required!
          </p>
          
          <button
            onClick={handleStartPreview}
            className="bg-indigo-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Start Free Preview Now
          </button>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Interactive Whiteboard</h3>
            <p className="text-sm text-gray-600">
              Visual concepts and diagrams drawn in real-time as you learn
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Code className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Live Code Editor</h3>
            <p className="text-sm text-gray-600">
              See code examples written and explained step-by-step
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Multiple Languages</h3>
            <p className="text-sm text-gray-600">
              Learn in Tamil, Hindi, Telugu, English, and more languages
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Ask Questions</h3>
            <p className="text-sm text-gray-600">
              Interact naturally - ask questions anytime during the session
            </p>
          </div>
        </div>

        {/* What to Expect */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            What to Expect in Your Preview
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Welcome & Introduction</h3>
                <p className="text-gray-600">
                  Meet your AI teacher and see how the platform works
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Whiteboard Demo</h3>
                <p className="text-gray-600">
                  Watch concepts being written and explained on the interactive whiteboard
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Live Coding Example</h3>
                <p className="text-gray-600">
                  See code written in real-time with clear explanations
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                4
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Ask Questions</h3>
                <p className="text-gray-600">
                  Try asking questions - experience the interactive teaching firsthand
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Experience the Future of Learning?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of students learning with AI-powered interactive teaching
          </p>
          <button
            onClick={handleStartPreview}
            className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Free Preview
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CoursePreviewPage;
